import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ErrorList from 'src/components/ErrorList/ErrorList'
import EyeCheckbox from 'src/components/EyeCheckbox/EyeCheckbox'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface PasswordInputFieldProps {
  errors?: Array<React.ReactElement | string>
  value?: string
  onChange: (value: string) => void
  className?: string
  fieldLabel?: React.ReactElement
  isNewPassword?: boolean
  placeHolder?: string
  id?: string
}

const PasswordInputField: React.FC<PasswordInputFieldProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setShowErrors(false)
    props.onChange(e.target.value)
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin-top: ${props.isNewPassword && '20px'};
      `}
    >
      <FieldLabel>
        {props.fieldLabel || <Trns source={'Password'} />}
      </FieldLabel>
      <div
        css={css`
          position: relative;
          display: flex;
          align-items: center;
          border-radius: 6px;
          height: 50px;
          border: 1px solid ${styleConstants.colors.gray_medium};
          background-color: ${styleConstants.colors.gray_lightest};

          &:focus,
          &:focus-within,
          &:hover {
            background-color: ${styleConstants.colors.white};
            border: 1px solid ${themeConstants.colors.cta};
          }

          &:hover > #${props.id ? props.id + 'Pwd' : 'pwd'} {
            background-color: ${styleConstants.colors.white};
          }

          ${showErrors &&
          css`
            border: 1px solid ${styleConstants.colors.red};
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )}

            &:focus,
          &:hover {
              border: 1px solid ${styleConstants.colors.red};
            }
          `}
        `}
      >
        <input
          id={props.id ? props.id + 'Pwd' : 'pwd'}
          data-testid="password-input"
          css={css`
            border-radius: 6px 0px 0px 6px;
            padding-left: 20px;
            border: none;
            background-color: ${styleConstants.colors.gray_lightest};
            width: 100%;
            height: 100%;
            caret-color: ${themeConstants.colors.cta};
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}

            &::placeholder {
              opacity: 0.5;
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            }

            &:focus,
            &:hover {
              background-color: ${styleConstants.colors.white};
              border: none;
            }

            &:-webkit-autofill,
            &:-webkit-autofill:hover,
            &:-webkit-autofill:focus,
            &:-webkit-autofill:active {
              transition: background-color 5000s;
            }
          `}
          placeholder={
            props.placeHolder
              ? props.placeHolder
              : translateSingleString(
                  locale,
                  'Enter a password for your profile',
                )
          }
          value={props.value}
          type={isPasswordVisible ? 'text' : 'password'}
          onChange={onChange}
          autoComplete={props.isNewPassword ? 'new-password' : 'password'}
        />
        {/* TODO: figure out how to change background of this element on autofill */}
        <EyeCheckbox
          css={css`
            padding-right: 30px;
            padding-left: 5px;
          `}
          isSelected={isPasswordVisible}
          onClick={(): void => {
            setIsPasswordVisible(!isPasswordVisible)
          }}
        />
      </div>
      {props.errors !== undefined && showErrors && (
        <ErrorList errors={props.errors} />
      )}
      {props.isNewPassword && (
        <span
          css={css`
            margin-top: 8px;
            padding: 0px 20px;
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns
            source={
              'Use 10 or more characters, with a mix of uppercase and lowercase letters, numbers & symbols. Spaces are not allowed.'
            }
          />
        </span>
      )}
    </div>
  )
}

export default React.memo(PasswordInputField)
